import React, { useMemo, useState, useEffect } from 'react';
import { Plus, X, AlertTriangle, Loader2, Eye } from 'lucide-react';
import type { QuestionOption, SubInputDefinition, MappingPair, SectionInfo, SectionReplacement } from './types';

interface AutoFixFormProps {
  option: QuestionOption;
  availableColumns: string[];
  initialParams: Record<string, any>;
  onParamsChange: (params: Record<string, any>) => void;
  onPreview: (params: Record<string, any>) => Promise<void> | void;
  previewSummary: { rowsChanged: number; total: number; summaryAction: string | null } | null;
  previewLoading: boolean;
  previewError: string | null;
  disabled?: boolean;
  sectionsByFile?: Record<string, SectionInfo[]>;
  questionScope?: { sectionRefs?: string[]; files?: string[] };
}

const FIELD_LABEL_CLASS = 'text-[12px] font-semibold text-[#0F172A]';
const HELP_CLASS = 'text-[11px] text-[#64748B] mt-0.5';
const INPUT_CLASS =
  'w-full mt-1 px-2.5 py-1.5 text-[13px] border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-[#0D9488] outline-none disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]';

function valueOrDefault(params: Record<string, any>, def: SubInputDefinition): any {
  if (params[def.key] !== undefined && params[def.key] !== null) return params[def.key];
  if (def.default !== undefined) return def.default;
  return def.type === 'boolean' ? false : '';
}

function isFieldFilled(value: any, def: SubInputDefinition): boolean {
  if (def.type === 'boolean') return true;
  if (def.type === 'mapping_list') {
    return Array.isArray(value) && value.some(p => (p?.original ?? '').toString().trim() !== '');
  }
  if (def.type === 'replacement_list') {
    return Array.isArray(value) && value.some(p => (p?.section_id ?? '').toString().trim() !== '');
  }
  if (def.type === 'column_multi_select' || def.type === 'section_multi_select') {
    return Array.isArray(value) && value.length > 0;
  }
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  return true;
}

const AutoFixForm: React.FC<AutoFixFormProps> = ({
  option,
  availableColumns,
  initialParams,
  onParamsChange,
  onPreview,
  previewSummary,
  previewLoading,
  previewError,
  disabled = false,
  sectionsByFile = {},
  questionScope = {},
}) => {
  const allFiles = Object.keys(sectionsByFile);
  const allSections: SectionInfo[] = allFiles.flatMap(f => sectionsByFile[f] || []);
  const scopedFiles = (questionScope.files && questionScope.files.length > 0)
    ? questionScope.files
    : allFiles;
  const scopedSections: SectionInfo[] = (() => {
    const refs = questionScope.sectionRefs || [];
    if (refs.length > 0) {
      return allSections.filter(s => refs.includes(s.section_id));
    }
    return scopedFiles.flatMap(f => sectionsByFile[f] || []);
  })();
  const fileOptions = scopedFiles.length > 0 ? scopedFiles : allFiles;
  const sectionOptions = scopedSections.length > 0 ? scopedSections : allSections;
  const subInputs = option.sub_inputs || [];
  const [params, setParams] = useState<Record<string, any>>(() => ({ ...(option.default_params || {}), ...initialParams }));

  useEffect(() => {
    const seeded = { ...(option.default_params || {}), ...initialParams };
    setParams(seeded);
    onParamsChange(seeded);
  }, [option.option_id]);

  const requiredMissing = useMemo(() => {
    const missing: string[] = [];
    for (const def of subInputs) {
      if (def.required) {
        const v = valueOrDefault(params, def);
        if (!isFieldFilled(v, def)) missing.push(def.label);
      }
    }
    return missing;
  }, [params, subInputs]);

  const setField = (key: string, value: any) => {
    setParams(prev => {
      const next = { ...prev, [key]: value };
      onParamsChange(next);
      return next;
    });
  };

  const renderInput = (def: SubInputDefinition) => {
    const value = valueOrDefault(params, def);

    if (def.type === 'text') {
      return (
        <input
          type="text"
          value={value || ''}
          placeholder={def.placeholder || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled}
          className={INPUT_CLASS}
        />
      );
    }

    if (def.type === 'number') {
      return (
        <input
          type="number"
          value={value === '' || value === undefined ? '' : value}
          onChange={e => setField(def.key, e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          className={INPUT_CLASS}
        />
      );
    }

    if (def.type === 'boolean') {
      return (
        <label className="inline-flex items-center gap-2 mt-1 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => setField(def.key, e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 rounded border-[#CBD5E1] text-[#0D9488] focus:ring-[#0D9488]"
          />
          <span className="text-[12px] text-[#475569]">{value ? 'Yes' : 'No'}</span>
        </label>
      );
    }

    if (def.type === 'select') {
      const normalizeOpt = (o: any): { value: string; label: string } => {
        if (typeof o === 'string') return { value: o, label: o };
        if (o && typeof o === 'object') return { value: String(o.value ?? ''), label: String(o.label ?? o.value ?? '') };
        return { value: String(o), label: String(o) };
      };
      return (
        <select
          value={value || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled}
          className={INPUT_CLASS}
        >
          <option value="">Choose…</option>
          {(def.options || []).map(rawOpt => {
            const opt = normalizeOpt(rawOpt);
            return (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            );
          })}
        </select>
      );
    }

    if (def.type === 'column_select') {
      return (
        <select
          value={value || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled || availableColumns.length === 0}
          className={INPUT_CLASS}
        >
          <option value="">Choose a column…</option>
          {availableColumns.map(col => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </select>
      );
    }

    if (def.type === 'column_multi_select') {
      const current: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="mt-1 border border-[#E2E8F0] rounded-lg p-2 max-h-40 overflow-y-auto bg-white">
          {availableColumns.length === 0 ? (
            <p className="text-[11px] text-[#94A3B8] italic">No columns detected.</p>
          ) : (
            availableColumns.map(col => {
              const checked = current.includes(col);
              return (
                <label key={col} className="flex items-center gap-2 py-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? current.filter(c => c !== col) : [...current, col];
                      setField(def.key, next);
                    }}
                    disabled={disabled}
                    className="w-3.5 h-3.5 rounded border-[#CBD5E1] text-[#0D9488] focus:ring-[#0D9488]"
                  />
                  <span className="text-[12px] text-[#334155] font-mono">{col}</span>
                </label>
              );
            })
          )}
        </div>
      );
    }

    if (def.type === 'long_text') {
      return (
        <textarea
          value={value || ''}
          placeholder={def.placeholder || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled}
          rows={6}
          className={`${INPUT_CLASS} font-mono`}
          style={{ resize: 'vertical', minHeight: '120px' }}
        />
      );
    }

    if (def.type === 'file_select') {
      return (
        <select
          value={value || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled || fileOptions.length === 0}
          className={INPUT_CLASS}
        >
          <option value="">{def.required ? 'Choose a file…' : 'All files (no limit)'}</option>
          {fileOptions.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      );
    }

    if (def.type === 'section_select') {
      return (
        <select
          value={value || ''}
          onChange={e => setField(def.key, e.target.value)}
          disabled={disabled || sectionOptions.length === 0}
          className={INPUT_CLASS}
        >
          <option value="">{def.required ? 'Choose a section…' : 'Whole document'}</option>
          {sectionOptions.map(s => (
            <option key={s.section_id} value={s.section_id}>
              [{s.section_id.split('::')[0]}] {s.heading}
            </option>
          ))}
        </select>
      );
    }

    if (def.type === 'section_multi_select') {
      const current: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="mt-1 border border-[#E2E8F0] rounded-lg p-2 max-h-52 overflow-y-auto bg-white">
          {sectionOptions.length === 0 ? (
            <p className="text-[11px] text-[#94A3B8] italic">No sections detected.</p>
          ) : (
            sectionOptions.map(s => {
              const checked = current.includes(s.section_id);
              return (
                <label key={s.section_id} className="flex items-start gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? current.filter(x => x !== s.section_id) : [...current, s.section_id];
                      setField(def.key, next);
                    }}
                    disabled={disabled}
                    className="w-3.5 h-3.5 mt-0.5 rounded border-[#CBD5E1] text-[#0D9488] focus:ring-[#0D9488]"
                  />
                  <span className="text-[12px] text-[#334155]">
                    <span className="font-mono text-[10px] text-[#94A3B8]">[{s.section_id.split('::')[0]}]</span>{' '}
                    <span className="font-semibold">{s.heading}</span>
                  </span>
                </label>
              );
            })
          )}
        </div>
      );
    }

    if (def.type === 'replacement_list') {
      const items: Array<SectionReplacement & { file?: string | null }> = Array.isArray(value)
        ? value.map((p: any) => ({ section_id: p?.section_id ?? '', original_value: p?.original_value ?? '', file: p?.file ?? null }))
        : [];
      const update = (idx: number, patch: Partial<SectionReplacement & { file?: string | null }>) => {
        const next = [...items];
        next[idx] = { ...next[idx], ...patch };
        setField(def.key, next);
      };
      const remove = (idx: number) => {
        setField(def.key, items.filter((_, i) => i !== idx));
      };
      const add = () => {
        setField(def.key, [...items, { section_id: '', original_value: '', file: null }]);
      };
      return (
        <div className="space-y-1.5 mt-1">
          {items.length === 0 && (
            <p className="text-[11px] text-[#94A3B8] italic">No sections to update yet — add at least one below.</p>
          )}
          {items.map((it, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {it.file && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#E0E7FF] text-[#3730A3] whitespace-nowrap flex-shrink-0">
                  {it.file}
                </span>
              )}
              <select
                value={it.section_id}
                onChange={e => update(idx, { section_id: e.target.value })}
                disabled={disabled}
                className={`${INPUT_CLASS} mt-0`}
              >
                <option value="">Choose section…</option>
                {sectionOptions.map(s => (
                  <option key={s.section_id} value={s.section_id}>
                    [{s.section_id.split('::')[0]}] {s.heading}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Existing value"
                value={it.original_value}
                onChange={e => update(idx, { original_value: e.target.value })}
                disabled={disabled}
                className={`${INPUT_CLASS} mt-0`}
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                className="p-1 rounded text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0D9488] hover:text-[#0B7A70] disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Add section to update
          </button>
        </div>
      );
    }

    if (def.type === 'mapping_list') {
      const pairs: MappingPair[] = Array.isArray(value)
        ? value.map(p => ({ original: p?.original ?? '', canonical: p?.canonical ?? '' }))
        : [];
      const updatePair = (idx: number, patch: Partial<MappingPair>) => {
        const next = [...pairs];
        next[idx] = { ...next[idx], ...patch };
        setField(def.key, next);
      };
      const removePair = (idx: number) => {
        const next = pairs.filter((_, i) => i !== idx);
        setField(def.key, next);
      };
      const addPair = () => {
        setField(def.key, [...pairs, { original: '', canonical: '' }]);
      };
      return (
        <div className="space-y-1.5 mt-1">
          {pairs.length === 0 && (
            <p className="text-[11px] text-[#94A3B8] italic">No mappings yet — add at least one pair below.</p>
          )}
          {pairs.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Original value"
                value={p.original}
                onChange={e => updatePair(idx, { original: e.target.value })}
                disabled={disabled}
                className={`${INPUT_CLASS} mt-0`}
              />
              <span className="text-[#94A3B8] text-[12px]">→</span>
              <input
                type="text"
                placeholder="Canonical value"
                value={p.canonical}
                onChange={e => updatePair(idx, { canonical: e.target.value })}
                disabled={disabled}
                className={`${INPUT_CLASS} mt-0`}
              />
              <button
                type="button"
                onClick={() => removePair(idx)}
                disabled={disabled}
                className="p-1 rounded text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                title="Remove pair"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addPair}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0D9488] hover:text-[#0B7A70] disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Add mapping pair
          </button>
        </div>
      );
    }

    return null;
  };

  const canPreview = requiredMissing.length === 0 && !disabled;

  const claimsDisplay: Array<{ file?: string | null; section_id?: string; value?: string; heading?: string | null }> =
    Array.isArray(option.default_params?._claims_display) ? (option.default_params!._claims_display as any[]) : [];
  const showClaimsPicker = option.option_id === 'PROSE_FIX_PICK_WINNER' && claimsDisplay.length > 0;

  const pickClaim = (winnerValue: string, winnerFile: string | null) => {
    const next: Record<string, any> = { ...params, winner_value: winnerValue };
    next.replacements = claimsDisplay
      .filter(c => c.value !== winnerValue || c.file !== winnerFile)
      .map(c => ({ section_id: c.section_id, original_value: c.value, file: c.file || null }));
    setParams(next);
    onParamsChange(next);
  };

  return (
    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 space-y-3">
      {showClaimsPicker && (
        <div className="bg-white border border-[#C4B5FD] rounded-lg p-2.5 space-y-1.5">
          <div className="text-[12px] font-semibold text-[#5B21B6]">Pick the winning value</div>
          <div className="text-[11px] text-[#64748B]">
            Click a claim below — it becomes the canonical value, and every other listed section will be updated to match.
          </div>
          <div className="space-y-1 mt-1">
            {claimsDisplay.map((c, idx) => {
              const isPicked = (params.winner_value ?? '') === c.value;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => pickClaim(String(c.value ?? ''), c.file ?? null)}
                  disabled={disabled}
                  className={`w-full flex items-start gap-2 text-left px-2.5 py-2 rounded-md border transition-colors ${
                    isPicked
                      ? 'bg-[#F5F3FF] border-[#7C3AED] ring-1 ring-[#7C3AED]'
                      : 'bg-white border-[#E2E8F0] hover:border-[#C4B5FD] hover:bg-[#FAFAFB]'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span className={`mt-0.5 inline-block w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                    isPicked ? 'bg-[#7C3AED] border-[#7C3AED]' : 'bg-white border-[#CBD5E1]'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#E0E7FF] text-[#3730A3] whitespace-nowrap">
                        {c.file || 'unknown file'}
                      </span>
                      {c.heading && (
                        <span className="text-[10px] text-[#94A3B8] truncate">› {c.heading}</span>
                      )}
                    </div>
                    <div className="font-mono text-[12px] text-[#0F172A] break-words">{c.value}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="space-y-2.5">
        {subInputs.map(def => (
          <div key={def.key}>
            <label className={FIELD_LABEL_CLASS}>
              {def.label}
              {def.required && <span className="text-[#DC2626] ml-0.5">*</span>}
            </label>
            {renderInput(def)}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#E2E8F0]">
        <button
          type="button"
          onClick={() => onPreview(params)}
          disabled={!canPreview || previewLoading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${
            canPreview && !previewLoading
              ? 'bg-white border border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA]'
              : 'bg-[#F1F5F9] border border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed'
          }`}
        >
          {previewLoading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Previewing…
            </>
          ) : (
            <>
              <Eye className="w-3.5 h-3.5" /> Preview impact
            </>
          )}
        </button>

        {requiredMissing.length > 0 && (
          <p className="text-[11px] text-[#92400E] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Required: {requiredMissing.join(', ')}
          </p>
        )}
      </div>

      {previewSummary && !previewError && (() => {
        const isProseOption = option.option_id?.startsWith('PROSE_FIX_');
        const noun = isProseOption ? 'section' : 'row';
        return (
          <div
            className={`rounded-md px-3 py-2 text-[12px] ${
              previewSummary.summaryAction === 'VALIDATION_ERROR'
                ? 'bg-[#FEE2E2] border border-[#FECACA] text-[#991B1B]'
                : previewSummary.rowsChanged > 0
                ? 'bg-[#D1FAE5] border border-[#A7F3D0] text-[#065F46]'
                : 'bg-[#E0F2FE] border border-[#BAE6FD] text-[#0C4A6E]'
            }`}
          >
            {previewSummary.summaryAction === 'VALIDATION_ERROR' ? (
              <span>The handler reported a validation error. Adjust the inputs above and try again.</span>
            ) : previewSummary.rowsChanged > 0 ? (
              <span>
                <strong>{previewSummary.rowsChanged}</strong> {noun}{previewSummary.rowsChanged === 1 ? '' : 's'} will change when you Apply this fix.
              </span>
            ) : (
              <span>No {noun}s would change with the current settings.</span>
            )}
          </div>
        );
      })()}

      {previewError && (
        <p className={HELP_CLASS} style={{ color: '#991B1B' }}>
          <AlertTriangle className="w-3 h-3 inline mr-1" /> {previewError}
        </p>
      )}

      {!previewSummary && !previewError && !previewLoading && (
        <p className={HELP_CLASS}>Click <strong>Preview impact</strong> to see exactly how many rows this rule will change before applying it.</p>
      )}
    </div>
  );
};

export default AutoFixForm;
